export interface ParentIds {
    show_id: string;
    show_name: string;
    collection_id: string;
    collection_name: string;
    category_id: string;
    category_name: string;
}

export const EMPTY_PARENT_PAYLOAD: ParentIds = {
    show_id: '',
    show_name: '',
    collection_id: '',
    collection_name: '',
    category_id: '',
    category_name: '',
};

export type ItemsParentMutateAsync = (variables: {
    parentId: string;
    type: string;
}) => Promise<unknown>;

const PARENT_ENTITY_TYPES = new Set(['EPISODE', 'SHOW', 'COLLECTION']);

const cacheKey = (type: string, parentId: string) =>
    `${type.trim().toUpperCase()}:${String(parentId).trim()}`;

const entityLabel = (entity?: { id?: string; name?: string; title?: string }) =>
    entity?.name || entity?.title || '';

/** Normalize API response into stable parent id/name fields. */
export function normalizeParentsResponse(response: unknown): ParentIds {
    const payload =
        (response as { data?: Record<string, { id?: string; name?: string; title?: string }> })
            ?.data ?? (response as Record<string, { id?: string; name?: string; title?: string }>) ?? {};

    return {
        show_id: payload.show?.id ?? '',
        show_name: entityLabel(payload.show),
        collection_id: payload.collection?.id ?? '',
        collection_name: entityLabel(payload.collection),
        category_id: payload.category?.id ?? '',
        category_name: entityLabel(payload.category),
    };
}

export function pickParentPayloadFields(ids: ParentIds): ParentIds {
    return {
        show_id: ids.show_id || '',
        show_name: ids.show_name || '',
        collection_id: ids.collection_id || '',
        collection_name: ids.collection_name || '',
        category_id: ids.category_id || '',
        category_name: ids.category_name || '',
    };
}

export function createParentCache(): Map<string, ParentIds> {
    return new Map();
}

export async function resolveParent(
    mutateAsync: ItemsParentMutateAsync,
    cache: Map<string, ParentIds>,
    parentId: string,
    entityType: string
): Promise<ParentIds> {
    const id = String(parentId).trim();
    const type = entityType.trim().toUpperCase();
    if (!id || !PARENT_ENTITY_TYPES.has(type)) {
        return { ...EMPTY_PARENT_PAYLOAD };
    }

    const key = cacheKey(type, id);
    const cached = cache.get(key);
    if (cached) return cached;

    const response = await mutateAsync({ parentId: id, type });
    const normalized = pickParentPayloadFields(normalizeParentsResponse(response));
    cache.set(key, normalized);
    return normalized;
}

export async function resolveParentsForRequests(
    mutateAsync: ItemsParentMutateAsync,
    cache: Map<string, ParentIds>,
    requests: Array<{ parentId: string; type: string }>
): Promise<void> {
    await Promise.all(
        requests.map(({ parentId, type }) =>
            resolveParent(mutateAsync, cache, parentId, type)
        )
    );
}

function getFromCache(
    cache: Map<string, ParentIds>,
    parentId: string,
    entityType: string
): ParentIds {
    return cache.get(cacheKey(entityType, parentId)) ?? { ...EMPTY_PARENT_PAYLOAD };
}

/** Unique parent API requests for manual widget items (SHOW / EPISODE only). */
export function collectManualItemParentRequests(
    items: Array<{ id?: string | number; type?: string }>
): Array<{ parentId: string; type: string }> {
    const requests: Array<{ parentId: string; type: string }> = [];
    const seen = new Set<string>();

    items.forEach((item) => {
        const type = String(item.type || '').trim().toUpperCase();
        const id = String(item.id || '').trim();
        if (!id || (type !== 'SHOW' && type !== 'EPISODE')) return;

        const key = cacheKey(type, id);
        if (seen.has(key)) return;
        seen.add(key);
        requests.push({ parentId: id, type });
    });

    return requests;
}

/** Widget-level parent request when source is SHOW or COLLECTION. */
export function collectWidgetSourceParentRequest(
    sourceType: string,
    sourceId?: string | number | null
): Array<{ parentId: string; type: string }> {
    const id = String(sourceId ?? '').trim();
    if (!id) return [];

    const normalized = sourceType.trim().toLowerCase();
    if (normalized === 'show') return [{ parentId: id, type: 'SHOW' }];
    if (normalized === 'collection') return [{ parentId: id, type: 'COLLECTION' }];
    return [];
}

export function buildWidgetLevelParentFields(
    sourceType: string,
    cache: Map<string, ParentIds>,
    options: {
        sourceId?: string | number | null;
        sourceName?: string;
        categoryId?: string | number | null;
        categoryName?: string;
    } = {}
): ParentIds {
    const normalized = sourceType.trim().toLowerCase();
    const sourceId = String(options.sourceId ?? '').trim();
    const sourceName = options.sourceName || '';

    if (normalized === 'show' && sourceId) {
        const resolved = getFromCache(cache, sourceId, 'SHOW');
        return pickParentPayloadFields({
            show_id: sourceId,
            show_name: sourceName,
            collection_id: resolved.collection_id,
            collection_name: resolved.collection_name,
            category_id: resolved.category_id,
            category_name: resolved.category_name,
        });
    }

    if (normalized === 'collection' && sourceId) {
        const resolved = getFromCache(cache, sourceId, 'COLLECTION');
        return pickParentPayloadFields({
            show_id: '',
            show_name: '',
            collection_id: sourceId,
            collection_name: sourceName,
            category_id: resolved.category_id,
            category_name: resolved.category_name,
        });
    }

    if (normalized === 'category') {
        const categoryId = String(options.categoryId ?? options.sourceId ?? '').trim();
        return pickParentPayloadFields({
            show_id: '',
            show_name: '',
            collection_id: '',
            collection_name: '',
            category_id: categoryId,
            category_name: options.categoryName || sourceName,
        });
    }

    return { ...EMPTY_PARENT_PAYLOAD };
}

export function enrichWidgetItemsWithParents(
    widgetItems: Array<{
        redirection_to?: string;
        redirection_id?: string | number;
    }>,
    cache: Map<string, ParentIds>
): Array<Record<string, unknown>> {
    return widgetItems.map((item) => {
        const redirectionTo = String(item.redirection_to || '').trim().toUpperCase();
        const redirectionId = String(item.redirection_id || '').trim();

        if (!redirectionId || (redirectionTo !== 'SHOW' && redirectionTo !== 'EPISODE')) {
            return {
                ...item,
                ...EMPTY_PARENT_PAYLOAD,
            };
        }

        const resolved = pickParentPayloadFields(
            getFromCache(cache, redirectionId, redirectionTo)
        );

        if (redirectionTo === 'SHOW') {
            return {
                ...item,
                show_id: resolved.show_id || redirectionId,
                show_name: resolved.show_name,
                collection_id: resolved.collection_id,
                collection_name: resolved.collection_name,
                category_id: resolved.category_id,
                category_name: resolved.category_name,
            };
        }

        return {
            ...item,
            ...resolved,
        };
    });
}

const REDIRECTION_PARENT_TYPES = new Set(['SHOW', 'EPISODE', 'COLLECTION']);

/** Collect unique parent API requests for redirection-based items (e.g. HomePageBanner). */
export function collectRedirectionParentRequests(
    items: Array<{ redirection_to?: string; redirection_id?: string | number }>
): Array<{ parentId: string; type: string }> {
    const requests: Array<{ parentId: string; type: string }> = [];
    const seen = new Set<string>();

    items.forEach((item) => {
        const type = String(item.redirection_to || '').trim().toUpperCase();
        const id = String(item.redirection_id || '').trim();
        if (!id || !REDIRECTION_PARENT_TYPES.has(type)) return;

        const key = cacheKey(type, id);
        if (seen.has(key)) return;
        seen.add(key);
        requests.push({ parentId: id, type });
    });

    return requests;
}
// Used in Home Page Banner Carousel widget 
export function enrichRedirectionItemsWithParents(
    widgetItems: Array<{
        redirection_to?: string;
        redirection_id?: string | number;
    }>,
    cache: Map<string, ParentIds>
): Array<Record<string, unknown>> {
    return widgetItems.map((item) => {
        const redirectionTo = String(item.redirection_to || '').trim().toUpperCase();
        const redirectionId = String(item.redirection_id || '').trim();

        if (!redirectionId || !REDIRECTION_PARENT_TYPES.has(redirectionTo)) {
            return {
                ...item,
                ...EMPTY_PARENT_PAYLOAD,
            };
        }

        const resolved = pickParentPayloadFields(
            getFromCache(cache, redirectionId, redirectionTo)
        );
        
        if (redirectionTo === 'SHOW') {
            return {
                ...item,
                show_id: resolved.show_id || redirectionId,
                show_name: resolved.show_name,
                collection_id: resolved.collection_id,
                collection_name: resolved.collection_name,
                category_id: resolved.category_id,
                category_name: resolved.category_name,
            };
        }

        if (redirectionTo === 'COLLECTION') {
            return {
                ...item,
                show_id: '',
                show_name: '',
                collection_id: resolved.collection_id || redirectionId,
                collection_name: resolved.collection_name,
                category_id: resolved.category_id,
                category_name: resolved.category_name,
            };
        }

        return {
            ...item,
            ...resolved,
        };
    });
}

export function shouldResolveManualItemParents(
    contentType: string,
    sourceType: string
): boolean {
    return contentType === 'static' && sourceType === 'manual';
}

export function shouldResolveWidgetSourceParents(
    contentType: string,
    sourceType: string
): boolean {
    return contentType === 'static' && ['show', 'collection'].includes(sourceType);
}
